import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { fetchResearchTree, fetchResearchInProgress } from '@/api/research';
import { useSessionStore } from '@/stores/session.store';
import type { ResearchTreeView } from '@/types/research';

const SEEN_RESEARCH_KEY = 'theho.research.seenInProgress';

/**
 * Store partage pour la recherche du joueur courant.
 *
 * Sert principalement de *gate* dans l'UI : par exemple le bouton
 * "Galaxie" de la sidebar n'est visible que si la recherche
 * `r31_hyperdrive` a un niveau >= 1. Cf wiki §36.2 (cycle de vie planete)
 * et la conception "tech gating" du sitemap §32.
 *
 * Vue GLOBALE du joueur-univers (wiki §24.7-bis) : fetch sans planetId,
 * donc CurrentLevel = max des locaux = niveau GLOBAL connu par le joueur.
 * Les vues planetaires (ResearchView) refetchent localement avec planetId.
 *
 * Fonctionnement :
 * - Cache local de la ResearchTreeView avec map `code -> currentLevel`.
 * - Premier accès = lazy fetch (`/api/v1/research-tree?playerUniverseId=...`).
 * - Invalidation manuelle via `invalidate()` quand une recherche est lancee
 *   ou terminee (le composant qui declenche le start peut le faire).
 */
export const useResearchStore = defineStore('research', () => {
  const tree = ref<ResearchTreeView | null>(null);
  const loading = ref(false);
  let inflight: Promise<void> | null = null;

  /** Map { code -> currentLevel (= global) } reconstruite a partir de l'arbre. */
  const levelsByCode = computed<Record<string, number>>(() => {
    if (!tree.value) return {};
    const out: Record<string, number> = {};
    for (const cat of tree.value.categories) {
      for (const r of cat.researches) {
        out[r.code] = r.currentLevel;
      }
    }
    return out;
  });

  async function fetchIfNeeded(): Promise<void> {
    if (tree.value !== null || loading.value) return inflight ?? Promise.resolve();
    const session = useSessionStore();
    const pu = session.activePlayerUniverseId;
    if (pu == null) return; // pas en univers : pas de tree a charger
    loading.value = true;
    inflight = (async () => {
      try {
        // Sans planetId -> CurrentLevel = niveau GLOBAL du joueur.
        tree.value = await fetchResearchTree(pu);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[research-store] fetch failed', err);
      } finally {
        loading.value = false;
        inflight = null;
      }
    })();
    return inflight;
  }

  /** Vrai si la recherche dont le code est `code` a un niveau >= 1 (sauf
   * indication contraire via `minLevel`). Retourne false tant que l'arbre
   * n'a pas ete charge (= masque l'UI conditionnelle plutot que de
   * laisser fuiter un acces non-acquis). */
  function hasResearch(code: string, minLevel = 1): boolean {
    const lvl = levelsByCode.value[code];
    return typeof lvl === 'number' && lvl >= minLevel;
  }

  /** A appeler quand la recherche peut avoir change (start, completion). */
  function invalidate(): void {
    tree.value = null;
  }

  // --- Indicateur passif "nouvelle recherche au global" (menu) ---
  // Nombre de recherches EN COURS (file globale) + seuil deja "vu".
  const inProgressCount = ref(0);
  const seenResearch = ref<number>(Number(localStorage.getItem(SEEN_RESEARCH_KEY) ?? '0') || 0);
  const hasNewResearch = computed(() => inProgressCount.value > seenResearch.value);

  /** Marque la file de recherche comme vue (ouverture de la modale Recherche). */
  function acknowledgeResearch(): void {
    seenResearch.value = inProgressCount.value;
    try { localStorage.setItem(SEEN_RESEARCH_KEY, String(seenResearch.value)); } catch { /* ignore */ }
  }

  /** Rafraichit le compteur de recherches en cours (file globale). */
  async function refreshGlobalCount(): Promise<void> {
    const session = useSessionStore();
    const pu = session.activePlayerUniverseId;
    if (pu == null) { inProgressCount.value = 0; return; }
    try {
      const ip = await fetchResearchInProgress(pu);
      inProgressCount.value = ip.items.length;
      // Reajuste le seuil vu si la file a diminue (recherches terminees).
      if (seenResearch.value > inProgressCount.value) {
        seenResearch.value = inProgressCount.value;
        try { localStorage.setItem(SEEN_RESEARCH_KEY, String(seenResearch.value)); } catch { /* ignore */ }
      }
    } catch { /* silencieux */ }
  }

  return {
    tree,
    loading,
    levelsByCode,
    fetchIfNeeded,
    hasResearch,
    invalidate,
    inProgressCount,
    hasNewResearch,
    acknowledgeResearch,
    refreshGlobalCount,
  };
});
